import { useState, useEffect } from 'react';
import '../styles/UserAvatar.scss';

/**
 * UserAvatar — global reusable avatar component.
 *
 * Shows user.avatar_url if available; falls back to an initials circle
 * derived from first_name / last_name (or the first letter of name).
 * If the image URL is set but fails to load the fallback is also shown.
 *
 * Props:
 *   user      — user object { avatar_url?, first_name?, last_name?, name? }
 *   size      — diameter in px                  (default: 40)
 *   bordered  — adds a white ring + shadow       (default: false, use on dark/image backgrounds)
 *   className — extra class names on root element
 *   style     — extra inline styles on root element
 */

const getInitials = (user) => {
    if (!user) return '?';
    const first = user.first_name || user.name?.split(' ')[0] || '';
    const last  = user.last_name  || user.name?.split(' ').slice(1).join(' ') || '';
    const initials = [first[0], last[0]].filter(Boolean).join('').toUpperCase();
    return initials || '?';
};

const UserAvatar = ({
    user,
    size      = 40,
    bordered  = false,
    className = '',
    style     = {},
}) => {
    const [imgFailed, setImgFailed] = useState(false);

    const avatarSrc = user?.avatar_image || user?.avatar_url || null;

    // Reset failure flag whenever the source URL changes (e.g. after an upload)
    useEffect(() => { setImgFailed(false); }, [avatarSrc]);

    const initials  = getInitials(user);
    const fontSize  = Math.round(Math.max(size * 0.35, 10));

    const baseStyle = {
        width:        size,
        height:       size,
        borderRadius: '50%',
        flexShrink:   0,
        ...style,
    };

    const cls = [
        'user-avatar',
        bordered  && 'user-avatar--bordered',
        className,
    ].filter(Boolean).join(' ');

    if (avatarSrc && !imgFailed) {
        return (
            <img
                className={cls}
                src={avatarSrc}
                alt={[user.first_name, user.last_name].filter(Boolean).join(' ') || user.name || ''}
                style={{ ...baseStyle, objectFit: 'cover' }}
                onError={() => setImgFailed(true)}
            />
        );
    }

    return (
        <div
            className={`${cls} user-avatar--initials`}
            style={{ ...baseStyle, fontSize }}
            aria-label={`${initials} avatar`}
        >
            {initials}
        </div>
    );
};

export default UserAvatar;

